import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Catalog, OutOfStockList } from "../types/catalog";
import { parseCart, parseCatalog, parseDealList, parseOutOfStockList } from "../decoders";
import { Deal, StorefrontConfig, useStorefrontConfig } from "../types/storefront";
import React from "react";
import { Cart, CartItem } from "../types/cart";
import { storefrontCartsPath, storefrontDealsPath } from "src/routes";

const CATALOG_QUERY_KEY = "catalogData";
const OUTAGE_INFORMATION_QUERY_KEY = "outOfStockListData";
const CART_QUERY_KEY = "cartData";
const DEALS_QUERY_KEY = "dealsData";
const AGE_VERIFICATION_QUERY_KEY = "ageVerificationData";

class CannotFetchWithoutUrl extends Error {}

type UpdateCartParams = {
  updateCartUrl: string;
  csrfToken: string;
};

type UpdateAgeVerificationParams = {
  csrfToken: string;
}

const fetchCatalog = async (config: StorefrontConfig) : Promise<Catalog> => {
  const resp = await fetch(config.catalogUrl, { headers: { "Accept": "application/json" } });
  const data = await resp.json();
  const fullCatalog = parseCatalog(data);

  const filteredSections = fullCatalog.sections.filter(section => {
    return section.fulfillmentMethods.includes(config.fulfillmentMethod);
  });

  const filteredCatalog = fullCatalog.copyWith({ sections: filteredSections });
  return filteredCatalog;
}

const fetchOutageInfomation = async (config: StorefrontConfig): Promise<OutOfStockList> => {
  if (config.outageUrl === undefined)
    throw new CannotFetchWithoutUrl();

  const resp = await fetch(config.outageUrl, { headers: { "Accept": "application/json" } });
  const data = await resp.json();
  return parseOutOfStockList(config.loadedAt, data);
}

const fetchCart = async (config: StorefrontConfig): Promise<Cart | null> => {
  const resp = await fetch(
    storefrontCartsPath(config.storefrontId),
    { headers: { "Accept": "application/json" },
  });

  if (resp.ok) {
    const data = await resp.json();
    return parseCart(data);
  }

  return null;
}

const updateCart = async (params: UpdateCartParams & { newCartContent: CartItem[] }): Promise<Cart> => {
  const resp = await fetch(params.updateCartUrl, {
    method: "post",
    credentials: "same-origin",
    headers: {
      "X-CSRF-Token": params.csrfToken, 'Content-Type': 'application/json'
    },
    body: JSON.stringify({ bag: params.newCartContent.map((ci: CartItem) => ci.toJson()) }),
  });

  const json = await resp.json();
  return parseCart(json);
};

const fetchDeals = async (config: StorefrontConfig): Promise<Deal[]> => {
  const resp = await fetch(
    storefrontDealsPath(config.storefrontId),
    { headers: { "Accept": "application/json" },
  });

  const data = await resp.json();
  return parseDealList(data);
}

const updateAgeVerification = async (params: UpdateAgeVerificationParams): Promise<boolean> => {
  const response = await fetch("/age_verification", {
    method: "post",
    credentials: "same-origin",
    headers: {
      "X-CSRF-Token": params.csrfToken,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({}),
  });

  return response.redirected;
}

const useFetchCatalog = () => {
  const config = useStorefrontConfig();

  return useQuery<Catalog, string>({
    queryKey: [CATALOG_QUERY_KEY],
    queryFn: () => fetchCatalog(config),
    refetchOnWindowFocus: false,
  });
}

const useFetchOutOfStockList = () => {
  const config = useStorefrontConfig();

  return useQuery<OutOfStockList, string>({
    queryKey: [OUTAGE_INFORMATION_QUERY_KEY],
    queryFn: () => fetchOutageInfomation(config),
    enabled: !!config.outageUrl,
    refetchOnWindowFocus: false,
  });
}

const useFetchCart = () => {
  const config = useStorefrontConfig();

  const query = useQuery<Cart | null, string>({
    queryKey: [CART_QUERY_KEY],
    queryFn: () => fetchCart(config),
    refetchOnWindowFocus: false,
    initialData: null,
  });

  React.useEffect(() => {
    if (query.data !== undefined)
      updateCartItemCountIndicator(query.data);
  }, [query.data]);

  return query;
}

const useMutateCart = (params: UpdateCartParams) => {
  const queryClient = useQueryClient();

  return useMutation<Cart, string, CartItem[]>({
    mutationFn: (newCartItems: CartItem[]) => updateCart({
      updateCartUrl: params.updateCartUrl,
      csrfToken: params.csrfToken,
      newCartContent: newCartItems,
    }),
    onSuccess: (newCart) => {
      queryClient.setQueryData([CART_QUERY_KEY], newCart);
      updateCartItemCountIndicator(newCart);
    },
  });
}

const useFetchDeals = () => {
  const config = useStorefrontConfig();

  return useQuery<Deal[], string>({
    queryKey: [DEALS_QUERY_KEY],
    queryFn: () => fetchDeals(config),
    refetchOnWindowFocus: false,
  });
};

const useFetchAgeVerificationStatus = (initialValue: boolean) => {
  return useQuery<boolean, string>({
    queryKey: [AGE_VERIFICATION_QUERY_KEY],
    queryFn: () => false,
    initialData: initialValue,
    enabled: false,
  });
};

const useMutateAgeVerificationStatus = (params: UpdateAgeVerificationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => updateAgeVerification({
      csrfToken: params.csrfToken
    }),
    onSuccess: (newIsVerified) => {
      queryClient.setQueryData([AGE_VERIFICATION_QUERY_KEY], newIsVerified);
    },
  });
};

// This method *really* doesn't belong here, but there isn't a better home just yet.
const updateCartItemCountIndicator = (newCart: Cart | null) => {
  // Sync up cart item count on bottom nav bar
  const element = document.querySelector("#bottom-nav-cart-quantity");
  const countElement = document.querySelector("#bottom-nav-cart-quantity > span");

  if (!element || !countElement) return;

  const itemCount = newCart?.cartItemCount() || 0;
  countElement.innerHTML = itemCount.toString();

  if (itemCount == 0) {
    element.classList.add("is-completely-hidden");
  } else {
    element.classList.remove("is-completely-hidden");
  }
}

export {
  useFetchCatalog, useFetchOutOfStockList, useFetchDeals,
  useFetchCart, useMutateCart, useFetchAgeVerificationStatus, useMutateAgeVerificationStatus
};
